import { useEffect, useState } from 'react';
import type { MediaItem } from '../../shared/types';
import { ArrowIcon, CloseIcon, DownloadIcon, PlayIcon, TrashIcon } from './Icons';

interface LightboxProps {
  item: MediaItem;
  position: number;
  count: number;
  onClose: () => void;
  onMove: (direction: -1 | 1) => void;
  onDelete: (item: MediaItem) => void;
}

const formatDate = (date: string) =>
  new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(date));

export function Lightbox({ item, position, count, onClose, onMove, onDelete }: LightboxProps) {
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);

  useEffect(() => {
    setVideoFailed(false);
    setVideoStarted(false);
  }, [item.id]);

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onMove(-1);
      if (event.key === 'ArrowRight') onMove(1);
    };

    document.body.classList.add('lightbox-open');
    window.addEventListener('keydown', keydown);

    return () => {
      document.body.classList.remove('lightbox-open');
      window.removeEventListener('keydown', keydown);
    };
  }, [onClose, onMove]);

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={`${item.name} viewer`}>
      <header className="lightbox__header">
        <span>
          {position + 1} / {count}
        </span>
        <div className="lightbox__actions">
          <a className="icon-button" href={item.downloadUrl} aria-label="Download original">
            <DownloadIcon />
          </a>
          <button className="icon-button" type="button" onClick={() => onDelete(item)} aria-label="Move to Trash">
            <TrashIcon />
          </button>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close viewer" autoFocus>
            <CloseIcon />
          </button>
        </div>
      </header>

      <div className="lightbox__stage">
        {item.mediaType === 'photo' ? (
          <img src={item.thumbnailUrl} alt={item.name} />
        ) : videoFailed ? (
          <div className="video-fallback">
            <p>This video format can’t play in this browser.</p>
            <a className="button button--light" href={item.downloadUrl}>
              <DownloadIcon /> Download original
            </a>
          </div>
        ) : videoStarted ? (
          <video
            src={item.contentUrl}
            controls
            autoPlay
            playsInline
            onError={() => setVideoFailed(true)}
          />
        ) : (
          <button
            className="lightbox__video-poster"
            type="button"
            onClick={() => setVideoStarted(true)}
            aria-label={`Play video: ${item.name}`}
          >
            <img src={item.thumbnailUrl} alt="" />
            <span className="lightbox__video-play" aria-hidden="true">
              <PlayIcon />
            </span>
          </button>
        )}

        {count > 1 && (
          <>
            <button className="lightbox__arrow lightbox__arrow--previous" type="button" onClick={() => onMove(-1)} aria-label="Previous item">
              <ArrowIcon direction="left" />
            </button>
            <button className="lightbox__arrow lightbox__arrow--next" type="button" onClick={() => onMove(1)} aria-label="Next item">
              <ArrowIcon direction="right" />
            </button>
          </>
        )}
      </div>

      <footer className="lightbox__footer">
        <div>
          <h2>{item.name}</h2>
          <p>
            {formatDate(item.capturedAt)} · uploaded by {item.uploader}
          </p>
        </div>
        {item.width && item.height && (
          <span>
            {item.width} × {item.height}
          </span>
        )}
      </footer>
    </div>
  );
}
